import React, { type ButtonHTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/classnames';

export interface ButtonCustomProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  icon?: ReactNode;
  iconClassName?: string;
  iconPosition?: 'left' | 'right';
  title?: ReactNode;
  contentClassName?: string;
  w?: 'fit' | 'full';
  variant?: 'default' | 'outline' | 'second' | 'unstyled';
  loading?: boolean;
}

const ButtonCustom = React.forwardRef<HTMLButtonElement, ButtonCustomProps>(
  (
    {
      icon,
      iconClassName,
      iconPosition = 'left',
      title,
      contentClassName,
      w = 'fit',
      variant = 'default',
      className,
      children,
      loading = false,
      disabled,
      ...props
    },
    ref,
  ) => {
    let content = title || children;

    if (loading && typeof content === 'string') {
      content = `${content}...`;
    }

    const hasContent = content !== undefined && content !== null && content !== '';
    const sizeClasses =
      w === 'full' ? 'w-full h-[4rem]' : hasContent ? 'w-fit h-[3.8rem]' : 'w-[3.8rem] h-[3.8rem]';

    const variantClasses = {
      default:
        'bg-[#2563EB] text-action-primary hover:bg-[#6D96F2] active:hover:bg-[#1A46A7] disabled:bg-[#BBCFF9] disabled:dark:bg-[#9BB7F6] disabled:text-[#FFFFFF]',
      outline:
        'border border-primary bg-transparent text-second hover:bg-[#E9EFFD] hover:text-[#2563EB] active:bg-[#D7E3FF] active:text-[#1A46A7] focus-visible:ring-2 focus-visible:ring-[#2563EB]/30 disabled:border-disable disabled:bg-transparent disabled:text-disable dark:hover:bg-[#1C37E04D] dark:hover:text-[#9BB7F6] dark:active:bg-[#1C37E066] dark:active:text-[#BBCFF9]',
      second:
        'border border-main bg-action-secondary text-[1.6rem] font-semibold leading-[2.2rem] text-second hover:bg-[#E9EFFD] hover:text-[#2563EB] active:bg-[#D7E3FF] active:text-[#1A46A7] disabled:border-disable disabled:bg-transparent disabled:text-disable dark:border-primary dark:bg-action-secondary dark:hover:bg-[#1C37E04D] dark:hover:text-[#9BB7F6]',
      unstyled: '',
    };

    return (
      <>
        <button
          ref={ref}
          disabled={disabled || loading}
          className={cn(
            'inline-flex items-center justify-center rounded-[1rem] transition-all duration-200',
            'text-[1.4rem] md:text-[1.6rem]',
            hasContent ? 'px-4' : 'p-0',
            loading && 'cursor-not-allowed opacity-60',
            variantClasses[variant],
            sizeClasses,
            className,
          )}
          {...props}
        >
          {loading ? (
            <>
              <svg
                className="mr-2 h-[1.5rem] w-[1.5rem] shrink-0 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              {hasContent && <span className={cn('truncate', contentClassName)}>{content}</span>}
            </>
          ) : (
            <>
              {iconPosition === 'left' && icon && (
                <span
                  className={cn(
                    'flex shrink-0 items-center justify-center [&>img]:object-contain',
                    hasContent && 'mr-2',
                    iconClassName,
                  )}
                >
                  {icon}
                </span>
              )}

              {hasContent && <span className={cn('truncate', contentClassName)}>{content}</span>}

              {iconPosition === 'right' && icon && (
                <span
                  className={cn(
                    'flex shrink-0 items-center justify-center [&>img]:object-contain',
                    hasContent && 'ml-2',
                    iconClassName,
                  )}
                >
                  {icon}
                </span>
              )}
            </>
          )}
        </button>

        {loading &&
          typeof document !== 'undefined' &&
          createPortal(
            <div className="fixed inset-0 z-[99999] cursor-not-allowed" />,
            document.body,
          )}
      </>
    );
  },
);

ButtonCustom.displayName = 'ButtonCustom';

export default ButtonCustom;
